import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import { ROUTES } from 'Components/Breadcrumbs/routes';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { postNewVelocityParameter } from 'Services';
import { RQ_CREATE_VELOCITY_PARAMETER, RQ_PARAMETERS } from 'Utils';
import { FormikProps } from 'formik';
import { useHistory } from 'react-router-dom';
import { ReactQueryError } from '@brighterion/types/react-query';

const useAddVelocityParameter = (formRef: React.RefObject<FormikProps<ParameterVelocityFormSchema>>) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { push } = useHistory();
    const { addAlert } = useAlertBanner();
    const { addModal, addTechnicalErrorModal } = useReduxModal();

    const onSuccess = async (_data: any, variables: CreateVelocityParameterRequest) => {
        addAlert({
            id: 'add-velocity-parameter',
            content: t('CreateVelocityParameterSuccessModalText', {
                displayName: variables.message,
                status: variables.status
            }),
            status: 'success'
        });

        queryClient.invalidateQueries([RQ_PARAMETERS]);
        push(ROUTES.PARAMETERS.INDEX);
    };

    const onError = (error: ReactQueryError, variables: CreateVelocityParameterRequest) => {
        const errorData = error.response.data;

        switch (errorData.statusCode) {
            case 400:
                addModal({
                    id: 'add-velocity-parameter-400-error',
                    status: 'error',
                    title: t('Parameter could not be saved'),
                    content: (
                        <Trans
                            i18nKey='createVelocity400Error'
                            values={{
                                displayName: variables.fieldName,
                                errorMessage: errorData.error?.message ?? errorData.message
                            }}
                        />
                    ),
                    cancelText: t('Close')
                });

                break;
            default:
                addTechnicalErrorModal();
                break;
        }

        // retain user's values
        formRef.current?.resetForm({
            values: formRef.current.values,
            touched: formRef.current.touched,
            errors: formRef.current.errors,
            isSubmitting: false
        });
    };

    return useMutation([RQ_CREATE_VELOCITY_PARAMETER], postNewVelocityParameter, {
        onSuccess,
        onError
    });
};

export default useAddVelocityParameter;
